/**
 * Which screen a URL asks for.
 *
 * Path-based rather than hash-based, because the console's CloudFront
 * distribution already rewrites a 404 to `/index.html` with a 200 — so a deep
 * link works, and a support engineer can paste one into a ticket.
 *
 * A parser rather than a router library: there are six screens, and the whole
 * of the routing logic being a pure function means it can be tested without a
 * DOM. Add a seventh screen here and the exhaustive switch in `App` stops
 * compiling until it is rendered.
 *
 * Nothing here decides what a viewer may *see*. The API answers 404 for a tenant
 * out of scope exactly as for one that never existed, so a hand-typed URL for
 * another customer's deployment looks the same as a typo. That is the whole of
 * the scope guarantee; a check here would be a convenience, not a control.
 */

export type View =
  /**
   * Every instance, across every customer — the front door since IM-104.
   *
   * It is the screen at `/` because it answers what anyone actually arrives
   * with: something is running, something failed, or someone is waiting on a
   * deploy. The customer list is still here, one click away, because onboarding
   * and registration are per-customer work; it simply is not what the tool is
   * opened for.
   */
  | { readonly screen: 'fleet' }
  | { readonly screen: 'customers' }
  | { readonly screen: 'customer'; readonly tenantId: string }
  | {
      readonly screen: 'deployment'
      readonly tenantId: string
      readonly deploymentId: string
    }
  /** One cloud account, and whether it is ready to deploy into. */
  | {
      readonly screen: 'account'
      readonly tenantId: string
      readonly accountKey: string
    }
  /**
   * One run, at its own address (IM-105).
   *
   * Under the tenant because a run is, and because the read is tenant-scoped —
   * but it is a destination in its own right: the point of giving a run a path
   * is that the path can be pasted into a ticket while the deploy is still
   * going wrong.
   */
  | {
      readonly screen: 'run'
      readonly tenantId: string
      readonly runId: string
    }
  /** The audit trail. Not under a tenant: it spans them, and filters to one. */
  | { readonly screen: 'audit' }
  /** Who may reach which customers. Not under a tenant: it spans them. */
  | { readonly screen: 'access' }

/** Path for a view, so links are built in one place rather than interpolated. */
export const pathFor = (view: View): string => {
  switch (view.screen) {
    case 'fleet':
      return '/'
    case 'customers':
      return '/customers'
    case 'customer':
      return `/tenants/${encodeURIComponent(view.tenantId)}`
    case 'deployment':
      return `/tenants/${encodeURIComponent(view.tenantId)}/deployments/${encodeURIComponent(
        view.deploymentId,
      )}`
    case 'run':
      return `/tenants/${encodeURIComponent(view.tenantId)}/runs/${encodeURIComponent(view.runId)}`
    case 'account':
      return `/tenants/${encodeURIComponent(view.tenantId)}/accounts/${encodeURIComponent(
        view.accountKey,
      )}`
    case 'audit':
      return '/audit'
    case 'access':
      return '/access'
  }
}

/**
 * Reads a view out of a path.
 *
 * Anything unrecognised is the customer list rather than an error page: the
 * paths this app produces are the ones above, so an unknown one is a stale
 * bookmark or a typo, and showing someone where they can go beats telling them
 * where they cannot.
 */
export const viewFor = (pathname: string): View => {
  const segments = pathname.split('/').filter((segment) => segment !== '')

  if (segments[0] === 'audit') return { screen: 'audit' }
  if (segments[0] === 'access') return { screen: 'access' }
  if (segments[0] === 'customers') return { screen: 'customers' }
  // Anything unrecognised lands on the fleet rather than an error page — the
  // same reasoning as before, now that the fleet is what `/` means.
  if (segments[0] !== 'tenants' || segments[1] === undefined) return { screen: 'fleet' }
  const tenantId = decodeURIComponent(segments[1])

  if (segments[2] === 'runs' && segments[3] !== undefined) {
    return { screen: 'run', tenantId, runId: decodeURIComponent(segments[3]) }
  }
  if (segments[2] === 'deployments' && segments[3] !== undefined) {
    return { screen: 'deployment', tenantId, deploymentId: decodeURIComponent(segments[3]) }
  }
  // An account key carries a colon (`aws:123456789012`), so it is encoded in the
  // path and decoded here rather than being split on.
  if (segments[2] === 'accounts' && segments[3] !== undefined) {
    return { screen: 'account', tenantId, accountKey: decodeURIComponent(segments[3]) }
  }
  return { screen: 'customer', tenantId }
}
